import classNames from "classnames";

import React, { KeyboardEvent, useEffect, useRef, useState } from "react";
import { useOnClickOutside } from "usehooks-ts";
import {
    BranchCode,
    BranchDetails,
    extendedBranchesDetails,
} from "../../ts/branches";

import { useFindOrganizationsMapStore, useMapStore, DEFAULT_ZOOM } from "../FindOrganizationsMap/store";
import CustomIcon from "../CustomIcon";
import SVGIcon from "../SVGIcon/SVGIcon";
import s from "./CustomDropdown.module.css";

export default function CustomDropdown({
    label,
    options,
    className,
    setBranch,
    animate = false,
}: {
    label: string;
    options: BranchDetails[];
    className?: string | object;
    setBranch: (branch: BranchDetails["code"]) => void;
    animate?: boolean;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] =
        useState<BranchDetails | null>();
    const { inputValue, resetBranschAndOrganizations, resetSearch } =
        useFindOrganizationsMapStore();
    const handleKeyDown = (event: unknown, option?: BranchDetails) => {
        const keyboardEvent = event as KeyboardEvent;

        if (keyboardEvent.key === "Escape") {
            setIsOpen(false);
        }

        if (keyboardEvent.key === "Enter" || keyboardEvent.key === "13") {
            if (!option) return;

            setIsOpen(false);
            handleOptionClick(option);
        }
    };
    
    const map = useMapStore((state) => state.map);

    useEffect(() => {
        if (typeof window === undefined) return;

        window.addEventListener("keydown", (e) => handleKeyDown(e));

        return () => {
            window.removeEventListener("keydown", (e) => handleKeyDown(e));
        };
    }, []);
    const toggleDropdown = (e, action: string) => {
        if (action === "close" && !!selectedOption) {
            e.preventDefault();
            setSelectedOption(null);
            resetSearch()
            map?.setZoom(DEFAULT_ZOOM);
            if (!!inputValue) setBranch("");
            else {
                resetBranschAndOrganizations();
            }
            e.stopPropagation();
            return;
        }
        if (e.target.nodeName === "BUTTON" && action === "button")
            setIsOpen(!isOpen);
    };

    const dropdownRef = useRef<HTMLDivElement>(null);

    const rootClasses = classNames(s.dropdown, {});
    const unorderedListClasses = classNames(s.menu, {
        [s.menuOpen]: isOpen,
        [s.menuClosed]: !isOpen,
    });

    const chevronClasses = classNames(s.chevron, {
        [s.chevron__open]: isOpen,
        [s.dontAnimate]: !animate,
        [s.chevron_closed]: !isOpen,
    });

    const handleClickOutside = () => {
        setIsOpen(false);
    };

    useOnClickOutside(dropdownRef, handleClickOutside);

    const handleOptionClick = (option: BranchDetails | undefined) => {
        if (!option) {
            setIsOpen(false);
            return;
        }

        setSelectedOption(option);
        setIsOpen(false);
        setBranch(option.code);
    };
    const sectorClasses = classNames(
        s.sector,
        "override__parent_hover",
        s.sector__selected,
        s.buttonSelected,
        {
            extendedBranchesDetails,
        },
    );
    return (
        <div className={`${rootClasses} ${className}`} ref={dropdownRef}>
            <button
                className={s.button}
                onClick={(e) => toggleDropdown(e, "button")}
                onKeyDown={(e) => handleKeyDown(e)}
                aria-haspopup={true}
                aria-expanded={isOpen}
            >
                {selectedOption ? (
                    <span className={`${sectorClasses} ${s.pointerEvents}`}>
                        <CustomIcon
                            icon={selectedOption.icon as BranchCode}
                            onClick={() => handleOptionClick(selectedOption)}
                        />
                    </span>
                ) : null}
                {selectedOption?.name ? (
                    <span className={s.buttonLabel}>
                        {selectedOption?.name}
                    </span>
                ) : (
                    label
                )}

                {!!selectedOption ? (
                    <SVGIcon
                        onClick={(e) => toggleDropdown(e, "close")}
                        icon="burgundyorangeclose"
                        color="--neutral-greyscale-600"
                        className={s.CloseIcon}
                    />
                ) : (
                    <SVGIcon
                        icon="chevron"
                        color="black"
                        className={chevronClasses}
                    />
                )}
            </button>

            {isOpen && (
                <ul className={unorderedListClasses} role="list">
                    {options
                        .map((option: BranchDetails, index: number) => {
                            const listItemClasses = classNames(s.item, {
                                [s.item__selected]: selectedOption === option,
                            });
                            const sectorClasses = classNames(
                                s.sector,
                                "override__parent_hover",
                                {
                                    [s.sector__selected]:
                                        selectedOption === option,
                                    extendedBranchesDetails,
                                },
                            );
                            return (
                                <li
                                    key={`${index}+${option.name}`}
                                    className={listItemClasses}
                                    tabIndex={0}
                                    onClick={() => handleOptionClick(option)}
                                    onKeyDown={(e) => handleKeyDown(e, option)}
                                >
                                    <span className={sectorClasses}>
                                        <CustomIcon
                                            icon={option.icon as BranchCode}
                                            onClick={() =>
                                                handleOptionClick(option)
                                            }
                                        />
                                    </span>
                                    {option.name}
                                </li>
                            );
                        })
                        .reverse()}
                </ul>
            )}
        </div>
    );
}
